import React, {PropsWithChildren, useEffect, useRef, useState, createRef} from "react";
import {
    Button,
    Container,
    Dropdown, DropdownItemProps,
    DropdownProps,
    Form,
    Header,
    Icon,
    Input,
    Label,
    Message,
    Modal,
    Segment, Table
} from "semantic-ui-react";
import agent from "../../agent";
import {UserFileType} from "../dashboard";
import { GeneralTitleCase, getGeneralTitle } from "../../utils/common";
import {textLabels} from "../../consts/textLabels";
import { ALLOWED_EXTENSIONS, ACCEPT_STRING, MAX_FILE_SIZE, MAX_FILE_COUNT, MAX_FILENAME_LENGTH } from "../../consts/fileConstants";

interface Props {
    onClose: (success: Boolean) => void;
    patientId: string;
}

const PatientFilesUpload = (props: Props) => {
    const [uploading, setUploading] = useState(false);
    const [files, setFiles] = useState<File[]>([]);
    const [userFileTypes, setUserFileTypes] = useState<UserFileType[]>([]);
    const [userFileType, setUserFileType] = useState<string | undefined>(undefined);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        async function fetchUserFileTypes() {
            let response = (await agent.UserFileTypeProvider.getList());
            setUserFileTypes(response.docs);
        }

        fetchUserFileTypes().then();
    }, []);

    function truncateFilename(name: string): string {
        const lastDot = name.lastIndexOf('.');
        if (lastDot === -1) {
            // Нет расширения
            return name.length > MAX_FILENAME_LENGTH ? name.substring(0, MAX_FILENAME_LENGTH) : name;
        }
        const baseName = name.substring(0, lastDot);
        const ext = name.substring(lastDot); // включая точку
        if (baseName.length <= MAX_FILENAME_LENGTH) {
            return name;
        }
        const truncatedBase = baseName.substring(0, MAX_FILENAME_LENGTH);
        return truncatedBase + ext;
    }

    function validateFiles(selectedFiles: File[]): { valid: File[], errors: string[] } {
        const result: { valid: File[], errors: string[] } = { valid: [], errors: [] };

        if (selectedFiles.length > MAX_FILE_COUNT) {
            result.errors.push(`Максимальное количество файлов для загрузки: ${MAX_FILE_COUNT} шт.`);
            return result;
        }

        for (const file of selectedFiles) {
            const extension = file.name.split('.').pop()?.toLowerCase();
            if (!extension || !ALLOWED_EXTENSIONS.includes(extension)) {
                result.errors.push(`Файл «${file.name}» имеет недопустимый формат. Разрешённые форматы: ${ALLOWED_EXTENSIONS.join(', ')}`);
                continue;
            }
            if (file.size > MAX_FILE_SIZE) {
                result.errors.push(`Файл «${file.name}» превышает максимальный размер ${MAX_FILE_SIZE / (1024 * 1024)} МБ`);
                continue;
            }
            result.valid.push(file);
        }

        return result;
    }

    function onFileSelect(event: React.ChangeEvent<HTMLInputElement>) {
        setErrorMessage(null);
        let _files: File[] = [];
        if (event.target.files) {
            for (let i = 0; i < event.target.files!.length; i++)
                _files.push(event.target.files[i]);
        }
        const { valid, errors } = validateFiles(_files);
        if (errors.length > 0) {
            setErrorMessage(errors.join('\n'));
        }
        // Обрезаем длинные имена файлов
        const truncatedFiles = valid.map(f => {
            const newName = truncateFilename(f.name);
            if (newName !== f.name) {
                return new File([f], newName, { type: f.type });
            }
            return f;
        });
        setFiles(truncatedFiles);
        if (valid.length === 0) {
            event.target.value = '';
        }
    }

    async function onUploadClick() {
        if ((files.length > 0) && userFileType) {
            setUploading(true);
            setErrorMessage(null);
            try {
                let res = true;
                for (const file of files) {
                    const fres = await agent.FileProvider.upload(file, userFileType, props.patientId);
                    if (!fres)
                        res = false;
                }
                if (res)
                    props.onClose(true);
            } finally {
                setUploading(false);
            }
        }
    }

    return (
        <Modal
            onClose={() => props.onClose(false)}
            open={true}
            closeIcon>
            <Modal.Header>Загрузка файла</Modal.Header>
            <Modal.Content>
                {errorMessage && (
                    <Message negative>
                        <Message.Header>Ошибка</Message.Header>
                        {errorMessage.split('\n').map((line, i) => (
                            <p key={i}>{line}</p>
                        ))}
                    </Message>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                    <Button
                        type={"button"}
                        onClick={() => fileInputRef.current?.click()}
                        content={"Выбрать файлы"}
                    />
                    {files.length > 0 && (
                        <span>Файлы: {files.length}</span>
                    )}
                </div>
                <input
                    ref={fileInputRef}
                    type={"file"}
                    multiple={true}
                    name={"file"}
                    accept={ACCEPT_STRING}
                    onChange={onFileSelect}
                    title=""
                    style={{ display: 'none' }}
                />
                {files.length > 0 && (
                    <Segment style={{ marginTop: '10px' }}>
                        {files.map((f, i) => (
                            <div key={i}>{f.name}</div>
                        ))}
                    </Segment>
                )}
                <h4>
                    Выберите тип файла
                </h4>

                <Dropdown
                    selection
                    search={true}
                    clearable={true}
                    value={userFileType}
                    style={{width: "100%"}}
                    onChange={(event: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
                        setUserFileType(data.value as string | undefined);
                    }}
                    options={userFileTypes.map((type) => ({
                        key: type._id,
                        text: type.name,
                        value: type._id,
                    } as DropdownItemProps))}
                    noResultsMessage={textLabels.noResultsMessage}
                />
            </Modal.Content>
            <Modal.Actions>
                <Button
                    content={"Загрузить"}
                    type={"submit"}
                    disabled={uploading || (files.length == 0) || !userFileType}
                    positive
                    onClick={onUploadClick}
                />
                <Button
                    color={'grey'}
                    disabled={uploading}
                    onClick={() => props.onClose(false)}>
                    {textLabels.cancelButton}
                </Button>
            </Modal.Actions>
        </Modal>
    );

};

export default PatientFilesUpload;